import { useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { IMapProps } from 'google-maps-react'
import useTranslation from 'next-translate/useTranslation'
import SearchIcon from '@ancon/wildcat-ui/shared/icons/i-search.svg'
import {
  CustomerAddress,
  CustomerDeliveryAddress,
  AddressLocationCoordinates,
} from '@ancon/wildcat-types'
import { isCoordinateWithinBounds } from '@ancon/wildcat-utils/location'
import getCustomerAddressFromGeocoderResult from '@ancon/wildcat-utils/user/getCustomerAddressFromGeocoderResult'

import useAppSelector from '../../../../store/hooks/useAppSelector'
import Input from '../../../app/components/input/Input'
import MapView from '../../../app/components/MapView'
import PlacesSuggestions from '../searchOutlets/suggestions/PlacesSuggestions'
import { outletDetailsDeliveryAreaSelector } from '../../../outlet/store/outletSelector'
import { GoogleMapsAPIUrl } from '../../../app/constants'
import { DeliveryLocationConfigureFormState } from '../../types'
import { ScriptStatus, useScript } from '../../../app/hooks/useScript'
import useGeocodeService from '../../../location/hooks/useGeocodeService'

import styles from './DeliveryLocationField.module.scss'

export default function DeliveryLocationField() {
  const { t } = useTranslation('common')

  const [searchTerm, setSearchTerm] = useState('')
  const [focused, setFocused] = useState(false)

  const outletDeliveryArea = useAppSelector(outletDetailsDeliveryAreaSelector)

  const { setValue, resetField } =
    useFormContext<DeliveryLocationConfigureFormState>()

  function validateDeliveryAddressBounds(address: CustomerDeliveryAddress) {
    if (
      address.location &&
      !!outletDeliveryArea?.locations?.length &&
      !!window.google &&
      !isCoordinateWithinBounds(
        address.location,
        outletDeliveryArea.locations,
        window.google.maps,
      )
    ) {
      return t(
        'components.deliveryLocationPickerModal.deliveryOutOfBounds',
      ).toString()
    }

    return true
  }

  const {
    field: { onChange, value: deliveryAddress },
    fieldState: { error },
  } = useController({
    name: 'address',
    rules: {
      required: t('common:validations.required'),
      validate: validateDeliveryAddressBounds,
    },
  })

  const searchResultsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const initialPlaceNameInitRef = useRef<boolean>(false)

  const { fetchGeocodeByPlaceId, fetchGeocodeResults } = useGeocodeService()

  const scriptStatus = useScript(GoogleMapsAPIUrl)

  // Set initial address place name
  useEffect(() => {
    async function setInitialPlaceName() {
      const geocode = await fetchGeocodeResults({
        latitude: deliveryAddress.location.latitude,
        longitude: deliveryAddress.location.longitude,
      })

      if (geocode) {
        initialPlaceNameInitRef.current = true
        setSearchTerm(geocode.formatted_address)
      }
    }

    if (
      deliveryAddress &&
      scriptStatus === ScriptStatus.Ready &&
      !initialPlaceNameInitRef.current
    ) {
      setInitialPlaceName()
    }
  }, [deliveryAddress, fetchGeocodeResults, scriptStatus])

  // Attach search results view focus listener
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      const searchResultsClicked =
        searchResultsRef.current &&
        searchResultsRef.current.contains(e.target as Node)
      const searchInputClicked =
        searchInputRef.current &&
        searchInputRef.current.contains(e.target as Node)

      if (!searchInputClicked && !searchResultsClicked) {
        setFocused(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  function setDeliveryLocationAddress(
    address: Omit<CustomerDeliveryAddress, 'location'> | null,
    position: AddressLocationCoordinates,
  ) {
    const deliveryLocationAddress: CustomerDeliveryAddress = {
      street: address?.street || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode || '',
      country: address?.country || '',
      location: position,
    }

    setValue('address', deliveryLocationAddress)
    onChange(deliveryLocationAddress)
  }

  function handleClearSearch() {
    setFocused(false)
    setSearchTerm('')
    resetField('address')
  }

  function handleSearchTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFocused(true)
    setSearchTerm(e.target.value)
  }

  function handleInputFocus() {
    setFocused(true)
  }

  async function handleAddressItemClick(
    address: CustomerAddress,
    googleResult: google.maps.GeocoderResult,
  ) {
    setFocused(false)

    const deliveryLocation = {
      city: address.city,
      country: address.country,
      state: address.state,
      street: address.street,
      zipCode: address.zipCode,
    }

    setDeliveryLocationAddress(deliveryLocation, address.location)
    setValue('instructions', address.note || '')
    setSearchTerm(googleResult.formatted_address)
  }

  async function handlePlaceSearchResultItemClick(
    placeId: string,
    description: string,
  ) {
    const geocode = await fetchGeocodeByPlaceId(placeId)

    if (geocode) {
      const addressFromGeocode = getCustomerAddressFromGeocoderResult(geocode)
      setFocused(false)

      const geocodeLocation = {
        latitude: geocode.geometry.location.lat(),
        longitude: geocode.geometry.location.lng(),
      }

      setDeliveryLocationAddress(addressFromGeocode, geocodeLocation)
      setSearchTerm(description)
    }
  }

  async function handleCurrentLocationClick(
    geocodeResult: google.maps.GeocoderResult,
  ) {
    setFocused(false)

    const address = getCustomerAddressFromGeocoderResult(geocodeResult)

    const geocodeLocation = {
      latitude: geocodeResult.geometry.location.lat(),
      longitude: geocodeResult.geometry.location.lng(),
    }

    setDeliveryLocationAddress(address, geocodeLocation)
    setSearchTerm(geocodeResult.formatted_address)
  }

  async function handleMarkerPositionChange(
    mapProps?: IMapProps,
    map?: google.maps.Map,
    event?: { latLng: google.maps.LatLng },
  ) {
    if (!event) return

    // Leave focus from search input
    setFocused(false)

    const newMarkerLocation = {
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng(),
    }

    const geocode = await fetchGeocodeResults(newMarkerLocation)

    if (geocode) {
      const address = getCustomerAddressFromGeocoderResult(geocode)

      setDeliveryLocationAddress(address, newMarkerLocation)
      setSearchTerm(geocode.formatted_address)
    }
  }

  return (
    <div className={styles.container}>
      <Input
        leftAccessory={<SearchIcon />}
        highlighted
        placeholder={t('components.deliveryLocationPickerModal.searchLocation')}
        clearable={!!searchTerm}
        dropdownView={
          focused ? (
            <div ref={searchResultsRef} className={styles.searchResults}>
              <PlacesSuggestions
                query={searchTerm}
                hideTitle
                onClickAccountAddress={handleAddressItemClick}
                onClickPlaceSearchResultItem={handlePlaceSearchResultItemClick}
                onClickCurrentLocation={handleCurrentLocationClick}
              />
            </div>
          ) : undefined
        }
        value={searchTerm}
        onChange={handleSearchTermChange}
        rightAccessoryOnClick={handleClearSearch}
        onFocus={handleInputFocus}
      />
      <div className={styles.mapViewContainer}>
        {scriptStatus === ScriptStatus.Ready && (
          <MapView
            polygonPoints={outletDeliveryArea?.locations}
            markerCoordinates={deliveryAddress.location}
            markerConfig={{
              draggable: true,
              onDragend: handleMarkerPositionChange,
            }}
            onClickPolygon={handleMarkerPositionChange}
          />
        )}
      </div>
      {!!error && <div className={styles.errorMessage}>{error.message}</div>}
    </div>
  )
}
